import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import '../styles/components/map-measurement-indicators.css';

const MapMeasurementIndicators = ({
  measurementMode,
  measurementPoints,
  measurements,
  showPointCloud
}) => {
  const map = useMap();

  useEffect(() => {
    // Don't show indicators if in point cloud view
    if (showPointCloud) return;

    // Create layer group for measurement indicators
    const measurementLayerGroup = L.layerGroup().addTo(map);

    // Add active measurement points
    measurementPoints.forEach((point, index) => {
      // For 3D points, we need to project them to lat/lng
      // Assuming the points are in a local coordinate system centered around the map center
      const mapCenter = map.getCenter();

      // Convert 3D coordinates to lat/lng (approximate conversion)
      // This assumes 1 unit = ~1 meter
      const lat = mapCenter.lat + (point[1] / 111000); // ~111km per degree latitude
      const lng = mapCenter.lng + (point[0] / (111000 * Math.cos(mapCenter.lat * Math.PI / 180)));

      // Create custom icon for measurement points
      const measurementIcon = L.divIcon({
        className: 'map-measurement-point',
        html: `
          <div class="map-measurement-marker active">
            <div class="map-measurement-pulse"></div>
            <div class="map-measurement-number">${index + 1}</div>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      });

      const marker = L.marker([lat, lng], { icon: measurementIcon });

      // Add popup with coordinates
      marker.bindPopup(`
        <div class="measurement-popup">
          <strong>Point ${index + 1}</strong><br/>
          <span class="coord">X: ${point[0].toFixed(3)}</span><br/>
          <span class="coord">Y: ${point[1].toFixed(3)}</span><br/>
          <span class="coord">Z: ${point[2].toFixed(3)}</span>
        </div>
      `);

      measurementLayerGroup.addLayer(marker);
    });

    // Draw lines for distance measurements
    if (measurementMode === 'distance' && measurementPoints.length === 2) {
      const mapCenter = map.getCenter();
      const latlngs = measurementPoints.map(point => [
        mapCenter.lat + (point[1] / 111000),
        mapCenter.lng + (point[0] / (111000 * Math.cos(mapCenter.lat * Math.PI / 180)))
      ]);

      const polyline = L.polyline(latlngs, {
        color: '#A1E430',
        weight: 3,
        opacity: 0.8,
        dashArray: '10, 5'
      });

      measurementLayerGroup.addLayer(polyline);
    }

    // Draw polygons for area/volume measurements
    if ((measurementMode === 'area' || measurementMode === 'volume') && measurementPoints.length >= 3) {
      const mapCenter = map.getCenter();
      const latlngs = measurementPoints.map(point => [
        mapCenter.lat + (point[1] / 111000),
        mapCenter.lng + (point[0] / (111000 * Math.cos(mapCenter.lat * Math.PI / 180)))
      ]);

      const polygon = L.polygon(latlngs, {
        color: '#A1E430',
        weight: 2,
        opacity: 0.8,
        fillColor: '#A1E430',
        fillOpacity: 0.2
      });

      measurementLayerGroup.addLayer(polygon);
    }

    // Add completed measurements
    measurements.forEach((measurement, idx) => {
      const mapCenter = map.getCenter();

      // Add markers for completed measurement points
      measurement.points.forEach((point, pointIdx) => {
        const lat = mapCenter.lat + (point[1] / 111000);
        const lng = mapCenter.lng + (point[0] / (111000 * Math.cos(mapCenter.lat * Math.PI / 180)));

        const completedIcon = L.divIcon({
          className: 'map-measurement-point',
          html: `
            <div class="map-measurement-marker completed">
              <div class="map-measurement-number">${pointIdx + 1}</div>
            </div>
          `,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });

        const marker = L.marker([lat, lng], { icon: completedIcon });
        measurementLayerGroup.addLayer(marker);
      });

      // Draw lines/polygons for completed measurements
      if (measurement.type === 'distance') {
        const latlngs = measurement.points.map(point => [
          mapCenter.lat + (point[1] / 111000),
          mapCenter.lng + (point[0] / (111000 * Math.cos(mapCenter.lat * Math.PI / 180)))
        ]);

        const polyline = L.polyline(latlngs, {
          color: '#4CAF50',
          weight: 2,
          opacity: 0.7
        });

        // Add label at midpoint
        const midLat = (latlngs[0][0] + latlngs[1][0]) / 2;
        const midLng = (latlngs[0][1] + latlngs[1][1]) / 2;

        const label = L.divIcon({
          className: 'map-measurement-label',
          html: `<div class="measurement-value">${measurement.formattedValue}</div>`,
          iconSize: [80, 20],
          iconAnchor: [40, 10]
        });

        const labelMarker = L.marker([midLat, midLng], { icon: label });
        measurementLayerGroup.addLayer(polyline);
        measurementLayerGroup.addLayer(labelMarker);
      } else if (measurement.type === 'area' || measurement.type === 'volume') {
        const latlngs = measurement.points.map(point => [
          mapCenter.lat + (point[1] / 111000),
          mapCenter.lng + (point[0] / (111000 * Math.cos(mapCenter.lat * Math.PI / 180)))
        ]);

        const polygon = L.polygon(latlngs, {
          color: '#4CAF50',
          weight: 2,
          opacity: 0.7,
          fillColor: '#4CAF50',
          fillOpacity: 0.15
        });

        // Calculate centroid for label
        const centroidLat = latlngs.reduce((sum, p) => sum + p[0], 0) / latlngs.length;
        const centroidLng = latlngs.reduce((sum, p) => sum + p[1], 0) / latlngs.length;

        const label = L.divIcon({
          className: 'map-measurement-label',
          html: `<div class="measurement-value">${measurement.formattedValue}</div>`,
          iconSize: [100, 20],
          iconAnchor: [50, 10]
        });

        const labelMarker = L.marker([centroidLat, centroidLng], { icon: label });
        measurementLayerGroup.addLayer(polygon);
        measurementLayerGroup.addLayer(labelMarker);
      }
    });

    // Cleanup function
    return () => {
      map.removeLayer(measurementLayerGroup);
    };
  }, [map, measurementMode, measurementPoints, measurements, showPointCloud]);

  return null;
};

export default MapMeasurementIndicators;